import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getLoyaltyOffers } from '@/api/loyalty';
import { LoyaltyAccount } from '@/features/loyalty/LoyaltyAccount';
import { LoyaltyPrompt } from '@/features/loyalty/LoyaltyPrompt';
import { LoyaltyRedeemed } from '@/features/loyalty/LoyaltyRedeemed';
import { LoyaltyOffersSkeleton } from '@/features/loyalty/LoyaltyOffersSkeleton';
import { LoyaltySignIn } from '@/features/loyalty/LoyaltySignIn';
import { loyaltyConfigurationQuery } from '@/features/loyalty/useLoyaltyConfiguration';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { useDelayedFlag } from '@/lib/useDelayedFlag';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import { useCheckout } from './checkoutContext';
import type { LoyaltyRedemption } from '@/api/types';

type Step =
    | { name: 'prompt' }
    | { name: 'signIn' }
    | { name: 'account'; accountId: string; signInToken: string | null }
    | { name: 'redeemed'; result: LoyaltyRedemption };

/**
 * The loyalty step, between the itemisation and the tip.
 *
 * One route with four states rather than four routes. Unlike tip and payment — which a guest genuinely goes
 * back and forth between, and which the browser's back button should move between — these are one
 * conversation: back out of the code field and you are abandoning a sign-in, not returning to a previous
 * decision. Four routes would also put a half-finished sign-in in the history, which is the one thing a guest
 * pressing back should not land in.
 *
 * Every exit leads to the tip. A guest who declines, has nothing to redeem, or redeems and confirms all end up
 * in the same place, because loyalty is a detour off the way to paying rather than a step of it.
 */
export function LoyaltyRoute() {
    const { location, check, accessToken } = useCheckout();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [step, setStep] = useState<Step>({ name: 'prompt' });

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    const configuration = useQuery(loyaltyConfigurationQuery(accessToken));

    const toTip = () => navigate('/tip');

    const offers = useQuery({
        queryKey: ['loyaltyOffers', check.id, step.name === 'account' ? step.accountId : null],
        queryFn: ({ signal }) =>
            getLoyaltyOffers(
                accessToken,
                (step as Extract<Step, { name: 'account' }>).accountId,
                (step as Extract<Step, { name: 'account' }>).signInToken,
                signal,
            ),
        enabled: step.name === 'account',
        retry: 1,
    });

    // Most accounts come back fast. A skeleton that flashes for 150ms reads as a glitch, so it waits.
    const showOffersSkeleton = useDelayedFlag(offers.isPending);

    // Reached directly, or the configuration failed. Either way there is nothing to ask, and the guest is on
    // their way to paying rather than looking at an error about a feature they did not ask for.
    if (configuration.isError || (configuration.data && !configuration.data.enabled)) {
        toTip();
        return <LoadingScreen />;
    }

    if (!configuration.data) return <LoadingScreen />;

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto flex w-full max-w-[var(--shell-max-width)] flex-col gap-4 px-5 pt-9 pb-12">
                {step.name === 'prompt' && (
                    <LoyaltyPrompt
                        configuration={configuration.data}
                        logoUrl={location.loyaltyLogoUrl}
                        onSignIn={() => setStep({ name: 'signIn' })}
                        onSkip={toTip}
                    />
                )}

                {step.name === 'signIn' && (
                    <LoyaltySignIn
                        configuration={configuration.data}
                        logoUrl={location.loyaltyLogoUrl}
                        accessToken={accessToken}
                        onSignedIn={(accountId, signInToken) =>
                            setStep({ name: 'account', accountId, signInToken })
                        }
                        onCancel={toTip}
                    />
                )}

                {step.name === 'account' &&
                    (offers.isPending ? (
                        /*
                         * A skeleton in place of the cards, not a full-page swap. The heading and the brand
                         * above it stay put, so the screen fills in rather than appearing to reload — and it
                         * only appears at all once the fetch has been slow enough to be worth mentioning.
                         */
                        showOffersSkeleton ? <LoyaltyOffersSkeleton /> : null
                    ) : offers.isError || !offers.data ? (
                        /*
                         * A lookup that fails is a guest who is not a member, or a vendor having a moment.
                         * Neither is worth stopping a checkout over, and neither is distinguishable from here.
                         */
                        <NotFound onContinue={toTip} />
                    ) : (
                        <LoyaltyAccount
                            configuration={configuration.data}
                            offers={offers.data}
                            accountId={step.accountId}
                            signInToken={step.signInToken}
                            accessToken={accessToken}
                            currency={location.currency}
                            locale={location.locale}
                            placeholderUrl={location.loyaltyPlaceholderUrl}
                            onRedeemed={(result) => {
                                /*
                                 * The check has changed at the POS. Dropping it from the cache means the tip
                                 * screen prices its percentages off the discounted total rather than the one
                                 * the guest saw a moment ago.
                                 */
                                void queryClient.invalidateQueries({ queryKey: ['check'] });

                                setStep({ name: 'redeemed', result });
                            }}
                            onSkip={toTip}
                        />
                    ))}

                {step.name === 'redeemed' && (
                    <LoyaltyRedeemed
                        result={step.result}
                        currency={location.currency}
                        locale={location.locale}
                        onContinue={toTip}
                    />
                )}
            </div>
        </div>
    );
}

function NotFound({ onContinue }: { onContinue: () => void }) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-6 text-center">
            <p className="text-[0.9375rem] text-pretty">{t('loyalty.notFound')}</p>
            <Button size="lg" onClick={onContinue}>
                {t('loyalty.continue')}
            </Button>
        </div>
    );
}
